/**
 * usePunchReminder
 *
 * Registers the punch-reminder service worker and keeps it in sync
 * with the current auth token. Call this hook once from App.js.
 *
 * Usage:
 *   import { usePunchReminder } from "./utils/usePunchReminder";
 *   // inside App():
 *   usePunchReminder();
 */

import { useEffect, useRef } from "react";
import Cookies from "js-cookie";
import { requestFcmToken } from "../firebase";

const SW_PATH = "/firebase-messaging-sw.js";

const getApiBase = () => {
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    if (hostname === "localhost" || hostname === "127.0.0.1" || hostname.startsWith("192.168.")) {
      return "http://localhost:6655/api";
    }
  }
  return "https://uptask.visiontrek.io/api";
};

const API_BASE = getApiBase();

let swReg = null; // module-level reference so we can re-use it

/**
 * Send a message to the service worker.
 */
function swPostMessage(payload) {
  if (!swReg?.active) return;
  swReg.active.postMessage(payload);
}

/**
 * Push the current auth token into the service worker.
 */
function syncToken() {
  const token = Cookies.get("token");
  if (token) {
    swPostMessage({ type: "STORE_TOKEN", token });
  }
}

/**
 * Tell the SW the user just punched in → suppress reminders.
 */
export function notifyPunchedIn() {
  localStorage.setItem("punched_in_today", "true");
  swPostMessage({ type: "PUNCHED_IN" });
}

export function usePunchReminder() {
  const registered = useRef(false);

  // Sync the browser's unique FCM token to the backend database
  const syncFcmToken = async () => {
    const authToken = Cookies.get("token");
    if (!authToken) return;

    try {
      const fcmToken = await requestFcmToken();
      if (fcmToken) {
        const res = await fetch(`${API_BASE}/attendance/store-fcm-token`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ token: fcmToken, deviceType: "web" }),
        });
        if (res.ok) {
          console.log("[PunchReminder] FCM Token successfully sent to backend.");
        }
      }
    } catch (err) {
      console.error("[PunchReminder] Error registering FCM Token on backend:", err);
    }
  };

  useEffect(() => {
    if (!("serviceWorker" in navigator)) {
      console.warn("[PunchReminder] Service workers not supported.");
      return;
    }
    if (!("Notification" in window)) {
      console.warn("[PunchReminder] Notifications not supported.");
      return;
    }

    // Register SW (only once per page load)
    if (!registered.current) {
      registered.current = true;

      navigator.serviceWorker
        .register(SW_PATH)
        .then(async (reg) => {
          swReg = reg;
          console.log("[PunchReminder] SW registered:", reg.scope);
        })
        .catch((err) => console.error("[PunchReminder] SW registration failed:", err));
    }

    // Register FCM token
    syncFcmToken();

    // Re-sync token whenever it changes (e.g. after login)
    const interval = setInterval(syncToken, 60_000); // every 1 min
    syncToken(); // immediate

    return () => {
      clearInterval(interval);
    };
  }, []);
}
